const BASE_URL = process.env.REACT_APP_BASE_URL ?? '';

const getAuth = () => localStorage.getItem('token') || '';

const getDefaultHeaders = (): HeadersInit => ({
	Authorization: `Bearer ${getAuth()}`,
	'Content-Type': 'application/json',
	Accept: 'application/json',
});

// Funzione per gestire errori di autenticazione
const handleResponseError = async (resp: Response): Promise<never> => {
	const respCode = resp.status;

	if (respCode === 403) {
		const data = await resp.json();
		if (data?.message === 'Password expired.') {
			localStorage.setItem('error', 'Password expired.');
			window.location.assign('/?reset-password');
		}
	} else if (respCode === 401 || respCode === 429) {
		if (respCode === 429) {
			localStorage.setItem('error', 'Too many attempts! Wait a few seconds.');
		}
		window.location.assign('/');
	} else if (respCode === 422) {
		const data = await resp.json()
		if (data?.message === "Validation errors") {
			const messages = Object.values(data.data)
				.flat() // unisce tutti gli array in un unico array
				.join('\n');
			throw new Error(messages);
		}

		throw new Error(`${data.message || 'Unknown error'}`);
	} else if (respCode === 500) {
		const data = await resp.json();
		if (data?.message === "Internal server error") {
			throw new Error("Errore interno del server. Riprova più tardi.");
		}
		if (data.message) throw new Error(`${data.message || 'Unknown error'}`);
	}

	throw new Error(`HTTP Error ${respCode}: ${resp.statusText}`);
};

export const defaultFetchAnon = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
	try {
		const resp = await fetch(BASE_URL + url, {
			...options,
			headers: {
				...(options.headers || {}), // Unisce eventuali headers personalizzati passati in options
			},
		});

		const data = await resp.json();
		// Se la risposta è un array o un oggetto senza `success`, restituiscila direttamente
		if (Array.isArray(data) || (typeof data === 'object' && data !== null && !('success' in data))) {
			return data as T;
		}

		// Se `success` è false, genera un errore
		if (!data.success) {
			throw new Error(`Errore durante il recupero dei dati: ${data.message || 'Errore sconosciuto'}`);
		}

		return data.data as T;
	} catch (error) {
		throw error;
	}
};

export const defaultFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
	try {
		const resp = await fetch(BASE_URL + url, {
			...options,
			headers: {
				...getDefaultHeaders(),
				...(options.headers || {}), // Unisce eventuali headers personalizzati passati in options
			},
		});

		if (!resp.ok) await handleResponseError(resp);

		const data = await resp.json();
		// Se la risposta è un array o un oggetto senza `success`, restituiscila direttamente
		if (Array.isArray(data) || (typeof data === 'object' && data !== null && !('success' in data))) {
			return data as T;
		}

		// Se `success` è false, genera un errore
		if (!data.success) {
			throw new Error(`Errore durante il recupero dei dati: ${data.message || 'Errore sconosciuto'}`);
		}

		return data.data as T;
	} catch (error) {
		throw error;
	}
};

export const defaultFetchWithMeta = async <T, M = unknown>(url: string, options: RequestInit = {}): Promise<{ data: T; meta: M }> => {
	try {
		const resp = await fetch(BASE_URL + url, {
			...options,
			headers: {
				...getDefaultHeaders(),
				...(options.headers || {}),
			},
		});

		if (!resp.ok) await handleResponseError(resp);

		const json = await resp.json();

		if (!json.success) {
			throw new Error(`Errore durante il recupero dei dati: ${json.message}`);
		}

		return { data: json.data as T, meta: json.meta as M };
	} catch (error) {
		throw error;
	}
};

export const defaultFetchDownload = async (url: string, options: RequestInit = {}) => {
	try {
		const resp = await fetch(BASE_URL + url, {
			...options,
			headers: {
				...getDefaultHeaders(),
				...(options.headers || {}),
			},
		});

		if (!resp.ok) await handleResponseError(resp);

		const blob = await resp.blob();

		// Recupera il nome del file dagli headers se disponibile
		const contentDisposition = resp.headers.get('Content-Disposition');
		const filename = contentDisposition
			? contentDisposition.split('filename=')[1]?.replace(/"/g, '') // Pulisce eventuali virgolette
			: 'download.xlsx'; // Default se il server non lo manda

		// Crea il link per il download
		const link = document.createElement('a');
		link.href = URL.createObjectURL(blob);
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(link.href);
		return { success: true };
	} catch (error) {
		return { success: false, error: error instanceof Error ? error.message : 'Errore sconosciuto' };
	}
};

const getNoJsonHeaders = (): HeadersInit => ({
	Authorization: `Bearer ${getAuth()}`,
})

export const defaultFetchImport = async (url: string, formData: FormData) => {
	try {
		const resp = await fetch(BASE_URL + url, {
			method: 'POST',
			headers: {
				...getNoJsonHeaders()
			},
			body: formData,
		});

		if (!resp.ok) await handleResponseError(resp);

		// Se la risposta è un successo, restituisci la risposta (oppure ulteriori logiche)
		const data = await resp.json();

		// Se non ci sono dati validi, lancia un errore
		if (!data || !data.success) {
			throw new Error(`Errore durante l'import: ${data?.message || 'Errore sconosciuto'}`);
		}

		return { success: true };

	} catch (error) {
		// Gestisci gli errori di rete o imprevisti
		console.error('Error during import:', error);
		return { success: false, error: error instanceof Error ? error.message : 'Errore sconosciuto' };
	}
};
